/**
 * Access decision for a single navigation, kept pure so it can be tested
 * without a router, a store or a live session.
 *
 * The rule this enforces, in order:
 *   1. Authenticate  - if the route needs any kind of session and there isn't
 *                      one, go to the appropriate login page.
 *   2. Authorize     - most privileged gate first: admin, then stock
 *                      management, then the container storefront role.
 *
 * Getting that order wrong is what broke the previous version of this logic.
 * The old `beforeEach` chain tested `requiresAuth && loggedIn` before it
 * tested `requiresAdminAuth`, and because /admin carries both flags the admin
 * gate was never evaluated at all. It also read `user.roles` before
 * establishing that `user` existed, which threw on the /scan/* routes because
 * those carry requiresStockManagementPermission without requiresAuth.
 */

const SCANNER_ROUTE = /^\/scan(?:\/|$)/

// containerStoreAccess is a *storefront* entitlement, so it gates the
// storefront only. Matching the bare substring 'container' would also catch
// /admin/container-orders and /account/orders/container/:id, which are gated
// on isAdmin and requiresAuth respectively.
const CONTAINER_STOREFRONT = /^\/container(?:\/|$)/

const ALLOW = { type: 'allow' }
const FORBIDDEN = { type: 'forbidden' }

const redirectTo = to => ({ type: 'redirect', to })

export function resolveRouteAccess ({
  path,
  user,
  requiresAuth,
  requiresAdminAuth,
  requiresStockManagementPermission
}) {
  const currentPath = path || ''
  const onScanner = SCANNER_ROUTE.test(currentPath)
  const loggedIn = !!user

  // The scanner app has its own login screen; sending its users to the
  // storefront login would strand them.
  const deny = () => (onScanner ? redirectTo('/scan/login') : FORBIDDEN)

  const needsSession = !!(
    requiresAuth ||
    requiresAdminAuth ||
    requiresStockManagementPermission
  )

  // 1. Authentication. Nothing below may touch `user` until this has passed.
  if (needsSession && !loggedIn) {
    return redirectTo(onScanner ? '/scan/login' : '/login')
  }

  if (!loggedIn) return ALLOW

  // `roles` is typed JSON in the backend schema, so it can arrive as null.
  const roles = Array.isArray(user.roles) ? user.roles : []

  // 2. Authorization.
  if (requiresAdminAuth && !user.isAdmin) return deny()

  if (requiresStockManagementPermission && !roles.includes('stockManagementAccess')) {
    return deny()
  }

  if (CONTAINER_STOREFRONT.test(currentPath) && !roles.includes('containerStoreAccess')) {
    return deny()
  }

  return ALLOW
}
